/* eslint-disable camelcase */

// node modules
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { connect } from 'react-redux';
import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';

// app modules: components
import Heading from 'components/Heading';
import Link from 'components/Link';

// app modules: props
import AuthorsProps from 'components/Authors/AuthorsProps';

// app modules: services
import formatter from 'services/formatter';

// assets
import CssExcerptItem from './ExcerptItem.scss';

export class ExcerptItemComponent extends PureComponent {
  static propTypes = {
    allCapsTitle: PropTypes.bool,
    authors: AuthorsProps.authors,
    className: PropTypes.string,
    dangerouslyRenderHtmlDescription: PropTypes.bool,
    description: PropTypes.string,
    displayType: PropTypes.string,
    floatRight: PropTypes.bool,
    gutter: PropTypes.number,
    headingLevel: PropTypes.number,
    image: PropTypes.string,
    imageAdornements: PropTypes.node,
    imageOrientation: PropTypes.oneOf(['h', 'v']),
    // eslint-disable-next-line react/forbid-prop-types
    imageProps: PropTypes.object,
    imageSize: PropTypes.string,
    link: PropTypes.string,
    loading: PropTypes.bool,
    onClick: PropTypes.func,
    title: PropTypes.string,
    truncateToFit: PropTypes.bool,
    withGutter: PropTypes.bool,
    wrapAroundImage: PropTypes.bool,
  };

  static defaultProps = {
    allCapsTitle: true,
    authors: null,
    className: '',
    dangerouslyRenderHtmlDescription: false,
    description: null,
    displayType: 'desktop',
    floatRight: false,
    gutter: 0,
    headingLevel: 3,
    image: null,
    imageAdornements: null,
    imageOrientation: 'v',
    imageProps: {},
    imageSize: 'medium',
    link: null,
    loading: false,
    onClick: () => {},
    title: null,
    truncateToFit: true,
    withGutter: false,
    wrapAroundImage: true,
  };

  getMobileViewDescription() {
    const { description, truncateToFit } = this.props;
    if (!truncateToFit) {
      return description;
    }
    const defaultDescription = description || '';

    // finds the end of first sentence or the end of string.
    const regex = /\b((?!=|\.).)+(.)\b/;

    const regexResult = defaultDescription.match(regex);

    let shortDescription = '';

    if (
      regexResult !== null &&
      regexResult.length &&
      regexResult.input.charAt(regexResult.input.length - 1) === '.'
    ) {
      shortDescription = regexResult[0].concat('.');
    } else {
      shortDescription = defaultDescription;
    }

    return shortDescription;
  }

  render() {
    const {
      allCapsTitle,
      authors,
      className,
      dangerouslyRenderHtmlDescription,
      description,
      displayType,
      floatRight,
      gutter,
      headingLevel,
      image,
      imageAdornements,
      imageOrientation,
      imageProps,
      imageSize,
      link,
      loading,
      onClick,
      title,
      withGutter,
      wrapAroundImage,
    } = this.props;

    const isWide = displayType !== 'mobile';
    const optimalDescription = isWide ? description : this.getMobileViewDescription();
    const gutterStyle = { marginLeft: gutter, marginRight: gutter };
    const authorBits =
      authors && authors.length ? (
        <p className="excerpt-item__paragraph excerpt-item__emphasized">
          {formatter.authorsList.toDOMWithLinks(authors)}
        </p>
      ) : null;

    let HeadingTag;
    let headingProps;
    if (allCapsTitle) {
      HeadingTag = Heading;
      headingProps = { level: headingLevel };
    } else {
      HeadingTag = 'h3';
      headingProps = { className: 'excerpt-item__plain-heading' };
    }
    const header = (
      <header className="margin-bottom-5">
        <HeadingTag {...headingProps}>
          {loading ? (
            <Skeleton width="250px" />
          ) : (
            <Link to={link} onClick={onClick}>
              {title}
            </Link>
          )}
        </HeadingTag>
        {loading && authorBits ? <Skeleton width="100px" /> : authorBits}
      </header>
    );
    return (
      <article
        style={gutterStyle}
        className={classNames('excerpt-item', className, {
          'excerpt-item--wide': isWide || loading || wrapAroundImage === false,
          'excerpt-item--with-gutter': withGutter,
        })}
      >
        {!isWide && imageOrientation === 'h' ? header : null}
        <figure
          className={classNames(
            'excerpt-item__figure',
            `excerpt-item__figure--${imageOrientation}`,
            { 'excerpt-item__figure--small': imageSize === 'small' },
            { 'excerpt-item__figure--right': !isWide && floatRight },
          )}
        >
          {imageAdornements}
          {loading ? (
            <Skeleton className="scale-with-grid" width="100%" height="100%" />
          ) : (
            <Link to={link} onClick={onClick}>
              <img
                loading="lazy"
                src={image}
                className="scale-with-grid"
                alt={title}
                {...imageProps}
              />
            </Link>
          )}
        </figure>

        <div className="excerpt-item__body">
          {isWide || imageOrientation !== 'h' ? header : null}

          {!loading && dangerouslyRenderHtmlDescription ? (
            <div
              className="excerpt-item__paragraph"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: optimalDescription,
              }}
            />
          ) : (
            <p className="excerpt-item__paragraph">
              {loading ? <Skeleton count={isWide ? 3 : 5} /> : optimalDescription}
            </p>
          )}
        </div>
      </article>
    );
  }
}

const mapStateToProps = state => ({
  displayType: state.dimensions.displayType,
});

export default connect(
  mapStateToProps,
  null,
)(withStyles(CssExcerptItem)(ExcerptItemComponent));
